import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi, warmUpBackend } from '../services/api';
const AuthContext = createContext(null);

// True only when the current page load is the return leg of a Microsoft
// sign-in redirect (auth response in the URL, or an MSAL interaction pending).
const isSsoReturn = () =>
  (window.location.hash && /(code|error|error_description)=/.test(window.location.hash)) ||
  Object.keys(sessionStorage).some((k) => k.startsWith('msal.') && k.includes('interaction.status'));

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const s = localStorage.getItem('silogate_user');
    return s ? JSON.parse(s) : null;
  });
  const [loading, setLoading] = useState(false);
  const [ssoError, setSsoError] = useState('');
  // Gate rendering while completing a Microsoft redirect so the router
  // cannot navigate and wipe the auth response from the URL.
  const [initializing, setInitializing] = useState(() => isSsoReturn());

  useEffect(() => {
    if (!initializing) return;
    (async () => {
      try {
        warmUpBackend(true);
        const { getMsalInstance } = await import('../auth/msalConfig');
        const msal = await getMsalInstance();
        const result = await msal.handleRedirectPromise();
        if (result && result.idToken) {
          const { data } = await authApi.microsoftLogin(result.idToken);
          if (data.success) {
            localStorage.setItem('silogate_token', data.token);
            localStorage.setItem('silogate_user', JSON.stringify(data.user));
            setUser(data.user);
          } else {
            setSsoError(data.error || 'Microsoft sign-in failed');
          }
        }
      } catch (err) {
        setSsoError(err.response?.data?.error || err.errorMessage || err.message || 'Microsoft sign-in failed');
      } finally {
        setInitializing(false);
      }
    })();
  }, [initializing]);

  const login = async (email, password) => {
    setLoading(true);
    // Fire DB warmup in parallel with auth — by the time the user lands on the
    // dashboard, the DB is already waking. Fire-and-forget; doesn't block login.
    warmUpBackend(true);
    try {
      const { data } = await authApi.login(email, password);
      if (data.success) { localStorage.setItem('silogate_token', data.token); localStorage.setItem('silogate_user', JSON.stringify(data.user)); setUser(data.user); return { success: true }; }
      return { success: false, error: data.error };
    } catch (err) { return { success: false, error: err.response?.data?.error || 'Connection failed' }; }
    finally { setLoading(false); }
  };
  // ── Entra ID SSO — full-page redirect to Microsoft, completed on return
  // by the handleRedirectPromise effect above. Redirect (not popup) because
  // Microsoft's COOP headers break popup handles in modern Chrome.
  const loginWithMicrosoft = async () => {
    setLoading(true);
    setSsoError('');
    try {
      const { getMsalInstance, loginRequest } = await import('../auth/msalConfig');
      const msal = await getMsalInstance();
      // Clear any lock left by an abandoned earlier attempt
      Object.keys(sessionStorage)
        .filter((k) => k.startsWith('msal.') && k.includes('interaction.status'))
        .forEach((k) => sessionStorage.removeItem(k));
      await msal.loginRedirect(loginRequest); // navigates away — nothing runs after this
      return { success: true };
    } catch (err) {
      setLoading(false);
      return { success: false, error: err.errorMessage || err.message || 'Microsoft sign-in failed' };
    }
  };
  const logout = () => { localStorage.removeItem('silogate_token'); localStorage.removeItem('silogate_user'); setUser(null); };
  const hasRole = (role) => user?.roles?.includes(role);
  const isAdmin = () => hasRole('Administrator');
  const isExecutive = () => hasRole('Executive') || isAdmin();
  const isCEO = () => hasRole('CEO');
  const isGroupExecutive = () => hasRole('GroupExecutive');
  const isDivisionHead = () => hasRole('DivisionHead');
  const getUserDivision = () => user?.division || '';
  const getUserCluster = () => user?.cluster || '';
  const canEditPlan = (plan) => {
    if (isAdmin()) return true;
    const isDraft = plan?.status === 'Draft' || plan?.status === 'InProgress';
    // EM can edit their own division's plan
    if (isDivisionHead() && plan?.division?.name === getUserDivision() && isDraft) return true;
    // Heads in the same division can edit in draft
    if (getUserDivision() && plan?.division?.name === getUserDivision() && isDraft) return true;
    return false;
  };
  const canApprovePlan = (plan) => {
    if (isAdmin()) return true;
    if (isCEO()) return true;
    // GE can approve plans in their cluster — check both possible paths
    if (isGroupExecutive()) {
      const planCluster = plan?.cluster?.name || plan?.division?.cluster?.name || '';
      if (planCluster && planCluster === getUserCluster()) return true;
    }
    return false;
  };
  const isOwnPlan = (plan) => {
    if (isAdmin()) return true;
    return isDivisionHead() && plan?.division?.name === getUserDivision();
  };
  if (initializing) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0c1b2e', color: '#94a3b8', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 900, color: '#fff', marginBottom: 8 }}>SiloGate</div>
          <div>Completing Microsoft sign-in&hellip;</div>
        </div>
      </div>
    );
  }
  return (<AuthContext.Provider value={{ user, login, loginWithMicrosoft, ssoError, logout, loading, hasRole, isAdmin, isExecutive, isCEO, isGroupExecutive, isDivisionHead, getUserDivision, getUserCluster, canEditPlan, canApprovePlan, isOwnPlan }}>{children}</AuthContext.Provider>);
}
export const useAuth = () => useContext(AuthContext);
